import { Tile, type Game } from "..";
import type { Position, PositionF, rangeLabel } from "../../data/types";
import { deltas, floodFill, inRange, maxD2 } from "../../lib/algo";
import { System } from "../../lib/core";

export type GridEvents = {};

export class GridSystem extends System<Game>()<GridEvents>([]) {
  scan(cfg: {
    mode:
      | "flood" // scan if connected
      | "nearest"; // scan in distance order
    from: Tile;
    valid: (tile: Tile) => boolean;
    count: number | null;
    range: rangeLabel | rangeLabel[] | null;
  }) {
    const game = this.game;
    let count = cfg.count;
    let tiles: Tile[] = [];
    if (cfg.mode === "flood") {
      tiles = floodFill(
        [cfg.from],
        (tile) => this.adjacent(tile.position),
        (tile) => {
          const d2 =
            (tile.position.x - cfg.from.position.x) ** 2 +
            (tile.position.y - cfg.from.position.y) ** 2;
          if (cfg.range !== null && !inRange(d2, cfg.range)) return false;
          if (!cfg.valid(tile)) return false;
          if (count != null && --count < 0) return false;
          return true;
        },
      );
    }
    if (cfg.mode === "nearest") {
      // deltas is sorted by d² ascending — use maxD2 as the iteration cutoff,
      // and inRange to filter (matters for ring arrays where intermediate d²
      // values are excluded).
      const cutoff = cfg.range === null ? Infinity : maxD2(cfg.range);
      for (const delta of deltas) {
        if (delta.d2 > cutoff) break;
        const pos = { x: cfg.from.position.x + delta.x, y: cfg.from.position.y + delta.y };
        const tile = this.tryAt(pos);
        if (!tile) continue;
        if (cfg.range !== null && !inRange(delta.d2, cfg.range)) continue;
        if (!cfg.valid(tile)) continue;
        tiles.push(tile);
        if (count !== null && tiles.length >= count) break;
      }
    }
    const items = tiles.map((t) => t.item).filter(Boolean);
    return { tiles, items };
  }

  tryAt(pos: Position): Tile | null {
    return this.game.state.tiles.get(Tile.key(pos)) ?? null;
  }

  at(pos: Position): Tile {
    const tile = this.tryAt(pos);
    if (!tile) {
      throw new Error(`Tile not found at position: ${pos.x},${pos.y}`);
    }
    return tile;
  }

  adjacent(pos: Position): Tile[] {
    const offsets = [
      { x: -1, y: 0 },
      { x: 1, y: 0 },
      { x: 0, y: -1 },
      { x: 0, y: 1 },
    ];
    return offsets
      .map((o) => this.tryAt({ x: pos.x + o.x, y: pos.y + o.y }))
      .filter(Boolean);
  }

  nearestTo(pos: Position | PositionF, tiles: Tile[]): Tile {
    const [tile] = [...tiles].sort((a, b) => {
      const ad2 = (a.position.x - pos.x) ** 2 + (a.position.y - pos.y) ** 2;
      const bd2 = (b.position.x - pos.x) ** 2 + (b.position.y - pos.y) ** 2;
      return ad2 - bd2 || a.position.y - b.position.y || a.position.x - b.position.x;
    });
    if (!tile) {
      throw new Error("No tiles to select nearest tile from");
    }
    return tile;
  }

  initialize(width: number, height: number): void {
    const game = this.game;
    game.state.size = { x: width, y: height };
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const tile = new Tile(
          {
            position: { x, y },
            itemUid: null,
          },
          game,
        );
        game.state.tiles.set(tile.key, tile);
      }
    }
  }
}
